import { Component, ComponentType, TooltipConfig } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { styleCloneService, StyleCloneScope } from "../../StyleCloneService";
import {
  multiSelectOperationService,
  MultiSelectOperation,
  MultiSelectOperationResult,
} from "../../MultiSelectOperationService";
import {
  BreakpointId,
  BREAKPOINT_ALIASES,
  parseBreakpointReference,
  getBreakpointDisplayName,
  type ResponsiveStyleChanges,
} from "@/types/ai";
// Phase 5.1: Import CommandStateManager for accurate dimension tracking
import { commandStateManager } from "../../CommandStateManager";
// Phase 6: Import FlexRelationshipService for parent-child awareness
import { flexRelationshipService } from "../../FlexRelationshipService";

// ============================================================================
// Phase 2.2: Relative Value Utilities
// ============================================================================

/**
 * Constraints for calculated values to ensure valid results
 */
const VALUE_CONSTRAINTS = {
  size: { min: 10, max: 2400 },      // Component size in pixels (allows large components but prevents extreme values)
  fontSize: { min: 8, max: 72 },     // Font size in pixels
  opacity: { min: 0, max: 1 },       // Opacity 0-1
  padding: { min: 0, max: 100 },     // Padding in pixels
  margin: { min: 0, max: 200 },      // Margin in pixels
  borderRadius: { min: 0, max: 500 }, // Border radius in pixels
  gap: { min: 0, max: 100 },         // Flex gap in pixels
};

/**
 * Clamp a value to stay within constraints
 */
function clampValue(value: number, type: keyof typeof VALUE_CONSTRAINTS): number {
  const { min, max } = VALUE_CONSTRAINTS[type];
  return Math.round(Math.max(min, Math.min(max, value)));
}

/**
 * Parse numeric value from component size (handles 'auto', percentages, etc.)
 */
function parseComponentSize(value: number | string | undefined, fallback: number = 100): number {
  if (value === undefined || value === 'auto') return fallback;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Parse numeric value from style property (handles 'px', '%', etc.)
 */
function parseStyleValue(value: unknown, fallback: number = 0): number {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
}

function formatCssDimension(value: string, unit: string | undefined): string {
  return `${value}${unit || 'px'}`;
}

/**
 * Get current component height from all possible sources
 * Priority: Phase 5.1 cached state > responsivePosition.breakpoints.desktop.style > style > size > fallback
 * Note: Default fallback is 300px (typical chart/container height) to avoid "taller" making things shorter
 */
function getCurrentHeight(component: Component, fallback: number = 300): number {
  // Phase 5.1: Check CommandStateManager first for accurate cached dimensions
  const cachedState = commandStateManager.getLastKnownState(component.id);
  if (cachedState && cachedState.resolvedHeight > 0) {
    console.log(`[getCurrentHeight] From Phase 5.1 cache: ${cachedState.resolvedHeight}`);
    return cachedState.resolvedHeight;
  }

  // Check responsivePosition first (flex containers use this)
  const desktopStyle = component.responsivePosition?.breakpoints?.desktop?.style;
  if (desktopStyle?.height !== undefined) {
    const parsed = parseStyleValue(desktopStyle.height, 0);
    if (parsed > 0) {
      console.log(`[getCurrentHeight] From responsivePosition.desktop.style: ${parsed}`);
      return parsed;
    }
  }

  // Check component.style
  if (component.style?.height !== undefined) {
    const parsed = parseStyleValue(component.style.height, 0);
    if (parsed > 0) {
      console.log(`[getCurrentHeight] From style: ${parsed}`);
      return parsed;
    }
  }

  // Check component.size
  if (component.size?.height !== undefined) {
    const parsed = parseComponentSize(component.size.height, 0);
    if (parsed > 0) {
      console.log(`[getCurrentHeight] From size: ${parsed}`);
      return parsed;
    }
  }

  console.log(`[getCurrentHeight] Using fallback: ${fallback}`);
  return fallback;
}

/**
 * Get current component width from all possible sources
 * Priority: Phase 5.1 cached state > responsivePosition.breakpoints.desktop.style > style > size > fallback
 * Note: Default fallback is 400px (reasonable component width) for auto/undefined dimensions
 */
function getCurrentWidth(component: Component, fallback: number = 400): number {
  // Phase 5.1: Check CommandStateManager first for accurate cached dimensions
  const cachedState = commandStateManager.getLastKnownState(component.id);
  if (cachedState && cachedState.resolvedWidth > 0) {
    console.log(`[getCurrentWidth] From Phase 5.1 cache: ${cachedState.resolvedWidth}`);
    return cachedState.resolvedWidth;
  }

  // Check responsivePosition first (flex containers use this)
  const desktopStyle = component.responsivePosition?.breakpoints?.desktop?.style;
  if (desktopStyle?.width !== undefined) {
    const parsed = parseStyleValue(desktopStyle.width, 0);
    if (parsed > 0) {
      console.log(`[getCurrentWidth] From responsivePosition.desktop.style: ${parsed}`);
      return parsed;
    }
  }

  // Check component.style
  if (component.style?.width !== undefined) {
    const parsed = parseStyleValue(component.style.width, 0);
    if (parsed > 0) {
      console.log(`[getCurrentWidth] From style: ${parsed}`);
      return parsed;
    }
  }

  // Check component.size
  if (component.size?.width !== undefined) {
    const parsed = parseComponentSize(component.size.width, 0);
    if (parsed > 0) {
      console.log(`[getCurrentWidth] From size: ${parsed}`);
      return parsed;
    }
  }

  console.log(`[getCurrentWidth] Using fallback: ${fallback}`);
  return fallback;
}

/**
 * Quick command handler for universal patterns that work across all component types
 * Provides instant response for common commands without AI backend calls
 */
export class QuickCommandHandler extends BaseCommandHandler {
  /**
   * Components with native tooltip systems (showTooltip: boolean).
   * These should NOT use the generic tooltip property.
   */
  private static readonly NATIVE_TOOLTIP_TYPES = new Set([
    ComponentType.CHART,
    ComponentType.BAR_CHART,
    ComponentType.LINE_CHART,
    ComponentType.PIE_CHART,
    ComponentType.SCATTER_CHART,
    ComponentType.AREA_CHART,
    ComponentType.HEATMAP_CHART,
    ComponentType.TREEMAP_CHART,
    ComponentType.GAUGE_CHART,
    ComponentType.VERTICAL_GAUGE_CHART,
    ComponentType.MAP_CHART,
  ]);

  constructor() {
    super();
    this.initializePatterns();
    this.initializeRelativePatterns();
  }

  /**
   * Check if component has a native tooltip system.
   */
  private hasNativeTooltipSystem(component: Component): boolean {
    return QuickCommandHandler.NATIVE_TOOLTIP_TYPES.has(component.type);
  }

  private initializePatterns(): void {
    // Visibility patterns
    this.commandPatterns.set(
      /^(hide|hidden)$/i,
      () => ({ style: { display: 'none' } })
    );

    this.commandPatterns.set(
      /^(show|visible|unhide)$/i,
      () => ({ style: { display: 'block' } })
    );

    // Opacity patterns
    this.commandPatterns.set(
      /^transparent$/i,
      () => ({ style: { opacity: 0 } })
    );

    this.commandPatterns.set(
      /^opaque$/i,
      () => ({ style: { opacity: 1 } })
    );

    this.commandPatterns.set(
      /^(\d+)%?\s*opacity$/i,
      (match) => ({ style: { opacity: parseInt(match[1]) / 100 } })
    );

    this.commandPatterns.set(
      /^opacity\s*(\d+)%?$/i,
      (match) => ({ style: { opacity: parseInt(match[1]) / 100 } })
    );

    // Direct color patterns (single word)
    this.commandPatterns.set(
      /^(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey|brown|cyan|magenta)$/i,
      (match) => ({ style: { color: match[1].toLowerCase() } })
    );

    // Background color shortcuts
    this.commandPatterns.set(
      /^bg\s+(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey|brown|cyan|magenta)$/i,
      (match) => ({ style: { backgroundColor: match[1].toLowerCase() } })
    );

    // Size patterns - IMPORTANT: Use 'size' not 'style' for dimensions
    // Components fill their wrapper, so size controls the wrapper dimensions
    this.commandPatterns.set(
      /^full\s*width$/i,
      () => ({ size: { width: '100%' } })
    );

    this.commandPatterns.set(
      /^half\s*width$/i,
      () => ({ size: { width: '50%' } })
    );

    this.commandPatterns.set(
      /^auto\s*size$/i,
      () => ({ size: { width: 'auto', height: 'auto' } })
    );

    // Width/Height shortcuts (canonical aliases: /w and /h)
    this.commandPatterns.set(
      /^w\s*(\d+)$/i,
      (match) => ({ size: { width: parseInt(match[1]) } })
    );

    // Height shortcut - require space OR minimum 2 digits to avoid conflict with h1-h6 headings
    this.commandPatterns.set(
      /^h\s+(\d+)$/i,  // Require space: "h 200"
      (match) => ({ size: { height: parseInt(match[1]) } })
    );
    this.commandPatterns.set(
      /^h(\d{2,})$/i,  // No space but 2+ digits: "h200" (min 10)
      (match) => ({ size: { height: parseInt(match[1]) } })
    );

    // Canonical alias shortcuts
    this.commandPatterns.set(
      /^c\s+(.+)$/i, // /c for color
      (match) => ({ style: { color: match[1].toLowerCase() } })
    );

    this.commandPatterns.set(
      /^bg\s+(.+)$/i, // /bg for background (already exists above)
      (match) => ({ style: { backgroundColor: match[1].toLowerCase() } })
    );

    this.commandPatterns.set(
      /^ts\s*(\d+)$/i, // /ts for text-size
      (match) => ({ properties: { fontSize: parseInt(match[1]) } })
    );

    this.commandPatterns.set(
      /^pd\s*(\d+)$/i, // /pd for padding
      (match) => ({ style: { padding: `${match[1]}px` } })
    );

    this.commandPatterns.set(
      /^mg\s*(\d+)$/i, // /mg for margin
      (match) => ({ style: { margin: `${match[1]}px` } })
    );

    this.commandPatterns.set(
      /^op\s*(\d+)$/i, // /op for opacity
      (match) => ({ style: { opacity: parseInt(match[1]) / 100 } })
    );

    this.commandPatterns.set(
      /^r\s+(.+)$/i, // /r for radius
      (match) => {
        const radiusMap: Record<string, string> = {
          'none': '0',
          'sm': '4px',
          'md': '8px',
          'lg': '12px',
          'xl': '16px',
          'full': '9999px'
        };
        const value = match[1];
        return { style: { borderRadius: radiusMap[value] || value } };
      }
    );

    this.commandPatterns.set(
      /^bd\s+(.+)$/i, // /bd for border
      (match) => {
        if (match[1] === 'none') {
          return { style: { border: 'none' } };
        }
        return { style: { border: match[1] } };
      }
    );

    this.commandPatterns.set(
      /^sh\s+(.+)$/i, // /sh for shadow
      (match) => {
        const shadowMap: Record<string, string> = {
          'none': 'none',
          'sm': '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          'md': '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          'lg': '0 10px 15px -3px rgb(0 0 0 / 0.1)',
          'xl': '0 20px 25px -5px rgb(0 0 0 / 0.1)'
        };
        return { style: { boxShadow: shadowMap[match[1]] || shadowMap.md } };
      }
    );

    this.commandPatterns.set(
      /^ft\s+(.+)$/i, // /ft for font
      (match) => ({ properties: { fontWeight: match[1] } })
    );

    this.commandPatterns.set(
      /^sc\s+(up|down)(?:\s+(\d+))?$/i, // /sc for scale
      (match, component) => {
        const direction = match[1].toLowerCase();
        const factor = parseFloat(match[2]) || 20;
        const multiplier = direction === 'up' ? (1 + factor / 100) : (1 - factor / 100);

        if (component?.type === ComponentType.TEXT) {
          const currentSize = component.properties?.fontSize || 16;
          return { properties: { fontSize: Math.round((currentSize as number) * multiplier) } };
        }
        return { style: { transform: `scale(${multiplier})` } };
      }
    );

    this.commandPatterns.set(
      /^ly\s+(.+)$/i, // /ly for layout
      (match) => ({ style: { display: match[1] === 'flex' ? 'flex' : 'grid' } })
    );

    this.commandPatterns.set(
      /^al\s+(.+)$/i, // /al for align
      (match) => ({ style: { textAlign: match[1].toLowerCase() } })
    );

    this.commandPatterns.set(
      /^st(?:\s+(horizontal|vertical|h|v))?$/i, // /st for stack
      (match) => {
        const direction = match[1]?.toLowerCase();
        const isHorizontal = direction === 'h' || direction === 'horizontal';
        return {
          style: { display: 'flex' },
          properties: {
            flexDirection: isHorizontal ? 'row' : 'column',
            gap: '1rem'
          }
        };
      }
    );

    this.commandPatterns.set(
      /^gp\s*(\d+)$/i, // /gp for gap
      (match) => ({ style: { gap: `${match[1]}px` } })
    );

    // Width patterns - natural language (supports both px and % values)
    this.commandPatterns.set(
      /^(?:set |change |make )?(?:the )?width (?:to )?(\d+(?:\.\d+)?)(px|%)?$/i,
      (match) => {
        const value = match[1];
        const unit = match[2] || 'px';
        const finalValue = unit === '%' ? `${value}%` : parseInt(value);
        return { size: { width: finalValue } };
      }
    );

    this.commandPatterns.set(
      /^width\s+(\d+(?:\.\d+)?)(px|%)?$/i,
      (match) => {
        const value = match[1];
        const unit = match[2] || 'px';
        const finalValue = unit === '%' ? `${value}%` : parseInt(value);
        return { size: { width: finalValue } };
      }
    );

    // Height patterns - natural language (supports both px and % values)
    this.commandPatterns.set(
      /^(?:set |change |make )?(?:the )?height (?:to )?(\d+(?:\.\d+)?)(px|%)?$/i,
      (match) => {
        const value = match[1];
        const unit = match[2] || 'px';
        const finalValue = unit === '%' ? `${value}%` : parseInt(value);
        return { size: { height: finalValue } };
      }
    );

    this.commandPatterns.set(
      /^height\s+(\d+(?:\.\d+)?)(px|%)?$/i,
      (match) => {
        const value = match[1];
        const unit = match[2] || 'px';
        const finalValue = unit === '%' ? `${value}%` : parseInt(value);
        return { size: { height: finalValue } };
      }
    );

    // Min/max dimension patterns for container sizing. These use style, not size,
    // because web/mobile containers reserve title/header space via CSS minHeight.
    this.commandPatterns.set(
      /^(?:set |change |make )?(?:the\s+)?(?:(?:selected|current|this)\s+)?(?:component|container|element)?\s*min(?:imum)?[-\s]*height\s*(?:to\s*)?(\d+(?:\.\d+)?)\s*(px|%|rem|em|vh|vw)?$/i,
      (match) => ({ style: { minHeight: formatCssDimension(match[1], match[2]) } })
    );

    this.commandPatterns.set(
      /^(?:set |change |make )?(?:the\s+)?(?:(?:selected|current|this)\s+)?(?:component|container|element)?\s*max(?:imum)?[-\s]*height\s*(?:to\s*)?(\d+(?:\.\d+)?)\s*(px|%|rem|em|vh|vw)?$/i,
      (match) => ({ style: { maxHeight: formatCssDimension(match[1], match[2]) } })
    );

    this.commandPatterns.set(
      /^(?:set |change |make )?(?:the\s+)?(?:(?:selected|current|this)\s+)?(?:component|container|element)?\s*min(?:imum)?[-\s]*width\s*(?:to\s*)?(\d+(?:\.\d+)?)\s*(px|%|rem|em|vh|vw)?$/i,
      (match) => ({ style: { minWidth: formatCssDimension(match[1], match[2]) } })
    );

    this.commandPatterns.set(
      /^(?:set |change |make )?(?:the\s+)?(?:(?:selected|current|this)\s+)?(?:component|container|element)?\s*max(?:imum)?[-\s]*width\s*(?:to\s*)?(\d+(?:\.\d+)?)\s*(px|%|rem|em|vh|vw)?$/i,
      (match) => ({ style: { maxWidth: formatCssDimension(match[1], match[2]) } })
    );

    this.commandPatterns.set(
      /^(?:set |change |make )?(?:the\s+)?(?:(?:selected|current|this)\s+)?(?:component|container|element)?\s*min(?:imum)?[-\s]*height\s*(?:to\s*)?(\d+(?:\.\d+)?)\s*(px|%|rem|em|vh|vw)?\s+and\s+padding\s*(?:to\s*)?(\d+(?:\.\d+)?)\s*(px|%|rem|em|vh|vw)?$/i,
      (match) => ({
        style: {
          minHeight: formatCssDimension(match[1], match[2]),
          padding: formatCssDimension(match[3], match[4])
        }
      })
    );

    // ============================================================================
    // Phase 5.4: Breakpoint-Aware Dimension Patterns
    // Patterns like "set mobile width to 100%", "tablet height 300", "desktop width 50%"
    // ============================================================================

    // "set mobile/tablet/desktop width to X" or "set mobile/tablet/desktop width X"
    this.commandPatterns.set(
      /^(?:set |change |make )?(mobile|tablet|desktop)\s+width\s+(?:to\s+)?(\d+(?:\.\d+)?)(px|%)?$/i,
      (match) => {
        const breakpoint = match[1].toLowerCase() as 'mobile' | 'tablet' | 'desktop';
        const value = match[2];
        const unit = match[3] || 'px';
        const finalValue = unit === '%' ? `${value}%` : parseInt(value);
        return {
          size: { width: finalValue },
          breakpoint
        };
      }
    );

    // "set mobile/tablet/desktop height to X" or "set mobile/tablet/desktop height X"
    this.commandPatterns.set(
      /^(?:set |change |make )?(mobile|tablet|desktop)\s+height\s+(?:to\s+)?(\d+(?:\.\d+)?)(px|%)?$/i,
      (match) => {
        const breakpoint = match[1].toLowerCase() as 'mobile' | 'tablet' | 'desktop';
        const value = match[2];
        const unit = match[3] || 'px';
        const finalValue = unit === '%' ? `${value}%` : parseInt(value);
        return {
          size: { height: finalValue },
          breakpoint
        };
      }
    );

    // "mobile/tablet/desktop width X" (shorthand without "set")
    this.commandPatterns.set(
      /^(mobile|tablet|desktop)\s+width\s+(\d+(?:\.\d+)?)(px|%)?$/i,
      (match) => {
        const breakpoint = match[1].toLowerCase() as 'mobile' | 'tablet' | 'desktop';
        const value = match[2];
        const unit = match[3] || 'px';
        const finalValue = unit === '%' ? `${value}%` : parseInt(value);
        return {
          size: { width: finalValue },
          breakpoint
        };
      }
    );

    // "mobile/tablet/desktop height X" (shorthand without "set")
    this.commandPatterns.set(
      /^(mobile|tablet|desktop)\s+height\s+(\d+(?:\.\d+)?)(px|%)?$/i,
      (match) => {
        const breakpoint = match[1].toLowerCase() as 'mobile' | 'tablet' | 'desktop';
        const value = match[2];
        const unit = match[3] || 'px';
        const finalValue = unit === '%' ? `${value}%` : parseInt(value);
        return {
          size: { height: finalValue },
          breakpoint
        };
      }
    );

    // "mobile/tablet/desktop full width"
    this.commandPatterns.set(
      /^(mobile|tablet|desktop)\s+full\s*width$/i,
      (match) => {
        const breakpoint = match[1].toLowerCase() as 'mobile' | 'tablet' | 'desktop';
        return {
          size: { width: '100%' },
          breakpoint
        };
      }
    );

    // "mobile/tablet/desktop half width"
    this.commandPatterns.set(
      /^(mobile|tablet|desktop)\s+half\s*width$/i,
      (match) => {
        const breakpoint = match[1].toLowerCase() as 'mobile' | 'tablet' | 'desktop';
        return {
          size: { width: '50%' },
          breakpoint
        };
      }
    );

    // Margin patterns
    this.commandPatterns.set(
      /^margin\s*(\d+)$/i,
      (match) => ({ style: { margin: `${match[1]}px` } })
    );

    this.commandPatterns.set(
      /^m\s*(\d+)$/i,
      (match) => ({ style: { margin: `${match[1]}px` } })
    );

    this.commandPatterns.set(
      /^no\s*margin$/i,
      () => ({ style: { margin: '0' } })
    );

    // Padding patterns
    this.commandPatterns.set(
      /^padding\s*(\d+)$/i,
      (match) => ({ style: { padding: `${match[1]}px` } })
    );

    this.commandPatterns.set(
      /^(?:set |change |make )?(?:the\s+)?(?:(?:selected|current|this)\s+)?(?:component|container|element)?\s*padding\s*(?:to\s*)?(\d+(?:\.\d+)?)\s*(px|%|rem|em|vh|vw)?$/i,
      (match) => ({ style: { padding: formatCssDimension(match[1], match[2]) } })
    );

    this.commandPatterns.set(
      /^p\s*(\d+)$/i,
      (match) => ({ style: { padding: `${match[1]}px` } })
    );

    this.commandPatterns.set(
      /^no\s*padding$/i,
      () => ({ style: { padding: '0' } })
    );

    // Border patterns
    this.commandPatterns.set(
      /^border$/i,
      () => ({ style: { border: '1px solid #ccc' } })
    );

    this.commandPatterns.set(
      /^no\s*border$/i,
      () => ({ style: { border: 'none' } })
    );

    this.commandPatterns.set(
      /^border\s+(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey|brown|cyan|magenta)$/i,
      (match) => ({ style: { border: `1px solid ${match[1].toLowerCase()}` } })
    );

    // Border radius patterns
    this.commandPatterns.set(
      /^rounded$/i,
      () => ({ style: { borderRadius: '8px' } })
    );

    this.commandPatterns.set(
      /^circle$/i,
      () => ({ style: { borderRadius: '50%' } })
    );

    this.commandPatterns.set(
      /^square$/i,
      () => ({ style: { borderRadius: '0' } })
    );

    // Shadow patterns
    this.commandPatterns.set(
      /^shadow$/i,
      (_match, component) => {
        // For text components, add textShadow; for others, add boxShadow
        if (component?.type === 'text') {
          return { style: { textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)' } };
        }
        return { style: { boxShadow: '0 2px 4px rgba(0,0,0,0.1)' } };
      }
    );

    this.commandPatterns.set(
      /^no\s*shadow$/i,
      (_match, component) => {
        // For text components, remove textShadow; for others, remove boxShadow
        if (component?.type === 'text') {
          return { style: { textShadow: 'none' } };
        }
        return { style: { boxShadow: 'none' } };
      }
    );

    // Alignment patterns (for flex containers)
    this.commandPatterns.set(
      /^center$/i,
      () => ({
        style: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }
      })
    );

    // Position patterns
    this.commandPatterns.set(
      /^absolute$/i,
      () => ({ style: { position: 'absolute' } })
    );

    this.commandPatterns.set(
      /^relative$/i,
      () => ({ style: { position: 'relative' } })
    );

    this.commandPatterns.set(
      /^fixed$/i,
      () => ({ style: { position: 'fixed' } })
    );

    // Z-index patterns
    this.commandPatterns.set(
      /^z\s*(\d+)$/i,
      (match) => ({ style: { zIndex: parseInt(match[1]) } })
    );

    this.commandPatterns.set(
      /^front$/i,
      () => ({ style: { zIndex: 9999 } })
    );

    this.commandPatterns.set(
      /^back$/i,
      () => ({ style: { zIndex: -1 } })
    );

    // Cursor patterns
    this.commandPatterns.set(
      /^pointer$/i,
      () => ({ style: { cursor: 'pointer' } })
    );

    this.commandPatterns.set(
      /^clickable$/i,
      () => ({ style: { cursor: 'pointer' } })
    );

    // Overflow patterns
    this.commandPatterns.set(
      /^scroll$/i,
      () => ({ style: { overflow: 'auto' } })
    );

    this.commandPatterns.set(
      /^no\s*scroll$/i,
      () => ({ style: { overflow: 'hidden' } })
    );

    // Common style resets
    this.commandPatterns.set(
      /^reset$/i,
      () => ({
        style: {
          margin: '0',
          padding: '0',
          border: 'none',
          background: 'transparent'
        }
      })
    );

    // Relative size modifiers
    // NOTE: "bigger" for size is handled in initializeRelativePatterns()
    // This pattern handles TEXT (font size) and non-text (actual size increase)
    this.commandPatterns.set(
      /^bigger$/i,
      (_, component) => {
        if (component.type === ComponentType.TEXT) {
          const currentSize = component.style?.fontSize || component.properties?.fontSize || 16;
          const numericSize = typeof currentSize === 'string' ? parseInt(currentSize) : (typeof currentSize === 'number' ? currentSize : 16);
          return { style: { fontSize: `${(numericSize as number) * 1.2}px` } };
        } else {
          // For non-text components, use Phase 5.1 cached dimensions for proper size increase
          const currentWidth = getCurrentWidth(component);
          const currentHeight = getCurrentHeight(component);
          const newWidth = Math.round(currentWidth * 1.5);
          const newHeight = Math.round(currentHeight * 1.5);
          return {
            size: {
              width: clampValue(newWidth, 'size'),
              height: clampValue(newHeight, 'size')
            }
          };
        }
      }
    );

    // NOTE: "smaller" for size is handled in initializeRelativePatterns()
    // This pattern only handles TEXT components (font size reduction)
    this.commandPatterns.set(
      /^smaller$/i,
      (_, component) => {
        if (component.type === ComponentType.TEXT) {
          const currentSize = component.style?.fontSize || component.properties?.fontSize || 16;
          const numericSize = typeof currentSize === 'string' ? parseInt(currentSize) : (typeof currentSize === 'number' ? currentSize : 16);
          return { style: { fontSize: `${Math.max(8, (numericSize as number) * 0.8)}px` } };
        } else {
          // For non-text components, use Phase 5.1 cached dimensions for proper size reduction
          const currentWidth = getCurrentWidth(component);
          const currentHeight = getCurrentHeight(component);
          const newWidth = Math.round(currentWidth * 0.75);
          const newHeight = Math.round(currentHeight * 0.75);
          return {
            size: {
              width: clampValue(newWidth, 'size'),
              height: clampValue(newHeight, 'size')
            }
          };
        }
      }
    );

    // Flex patterns
    this.commandPatterns.set(
      /^flex$/i,
      () => ({ style: { display: 'flex' } })
    );

    this.commandPatterns.set(
      /^flex\s*row$/i,
      () => ({
        style: {
          display: 'flex',
          flexDirection: 'row'
        }
      })
    );

    this.commandPatterns.set(
      /^flex\s*column$/i,
      () => ({
        style: {
          display: 'flex',
          flexDirection: 'column'
        }
      })
    );

    // Gap patterns
    this.commandPatterns.set(
      /^gap\s*(\d+)$/i,
      (match) => ({ style: { gap: `${match[1]}px` } })
    );

    // Quick positioning
    this.commandPatterns.set(
      /^top\s*left$/i,
      () => ({ style: { top: '0', left: '0' } })
    );

    this.commandPatterns.set(
      /^top\s*right$/i,
      () => ({ style: { top: '0', right: '0' } })
    );

    this.commandPatterns.set(
      /^bottom\s*left$/i,
      () => ({ style: { bottom: '0', left: '0' } })
    );

    this.commandPatterns.set(
      /^bottom\s*right$/i,
      () => ({ style: { bottom: '0', right: '0' } })
    );

    // ========================================================================
    // Phase 4.2: Vertical Gauge Specific Patterns
    // ========================================================================

    // Toggle scale visibility
    this.commandPatterns.set(
      /^(?:hide|turn off|remove)\s*(?:the\s*)?scale$/i,
      () => ({ properties: { showScale: false } })
    );

    this.commandPatterns.set(
      /^(?:show|turn on|display)\s*(?:the\s*)?scale$/i,
      () => ({ properties: { showScale: true } })
    );

    // Move scale position
    this.commandPatterns.set(
      /^(?:put |move |set )?scale\s*(?:on |to )?(?:the\s*)?(left|right)(?:\s*side)?$/i,
      (match) => ({ properties: { scalePosition: match[1].toLowerCase() } })
    );

    this.commandPatterns.set(
      /^scale\s*(?:on |to )?(?:the\s*)?(left|right)$/i,
      (match) => ({ properties: { scalePosition: match[1].toLowerCase() } })
    );

    // Toggle value visibility
    this.commandPatterns.set(
      /^(?:hide|turn off|remove)\s*(?:the\s*)?(?:value|number)$/i,
      () => ({ properties: { showValue: false } })
    );

    this.commandPatterns.set(
      /^(?:show|turn on|display)\s*(?:the\s*)?(?:value|number)$/i,
      () => ({ properties: { showValue: true } })
    );

    // Change value position
    this.commandPatterns.set(
      /^(?:put |move |show )?value\s*(?:at |in |to )?(?:the\s*)?(top|center|bottom)$/i,
      (match) => ({ properties: { showValue: true, valuePosition: match[1].toLowerCase() } })
    );

    this.commandPatterns.set(
      /^value\s*(?:at |in |to )?(?:the\s*)?(top|center|bottom)$/i,
      (match) => ({ properties: { showValue: true, valuePosition: match[1].toLowerCase() } })
    );

    // Change border width
    this.commandPatterns.set(
      /^(?:make |set )?border\s*(?:width\s*)?(\d+)\s*(?:px)?$/i,
      (match) => ({ properties: { borderWidth: parseInt(match[1]) } })
    );

    this.commandPatterns.set(
      /^(?:make )?border\s*(?:thicker|wider)$/i,
      (_, component) => {
        const currentWidth = (component?.properties?.borderWidth as number) || 2;
        return { properties: { borderWidth: currentWidth + 2 } };
      }
    );

    this.commandPatterns.set(
      /^(?:make )?border\s*(?:thinner|narrower)$/i,
      (_, component) => {
        const currentWidth = (component?.properties?.borderWidth as number) || 2;
        return { properties: { borderWidth: Math.max(0, currentWidth - 1) } };
      }
    );

    // Change border radius
    this.commandPatterns.set(
      /^(?:round|more rounded)\s*corners$/i,
      (_, component) => {
        const currentRadius = (component?.properties?.borderRadius as number) || 12;
        return { properties: { borderRadius: currentRadius + 4 } };
      }
    );

    this.commandPatterns.set(
      /^(?:sharp|sharper|less rounded)\s*corners$/i,
      (_, component) => {
        const currentRadius = (component?.properties?.borderRadius as number) || 12;
        return { properties: { borderRadius: Math.max(0, currentRadius - 4) } };
      }
    );

    this.commandPatterns.set(
      /^(?:border\s*)?radius\s*(\d+)\s*(?:px)?$/i,
      (match) => ({ properties: { borderRadius: parseInt(match[1]) } })
    );

    // Change fill opacity (for vertical gauges)
    // These patterns require "fill" prefix to distinguish from general component opacity
    this.commandPatterns.set(
      /^fill\s*(\d+)%?\s*(?:opacity)?$/i,
      (match) => ({ properties: { fillOpacity: parseInt(match[1]) / 100 } })
    );

    this.commandPatterns.set(
      /^fill\s*opacity\s*(\d+)%?$/i,
      (match) => {
        const value = parseInt(match[1]);
        return { properties: { fillOpacity: value > 1 ? value / 100 : value } };
      }
    );

    this.commandPatterns.set(
      /^(\d+)%\s*fill(?:\s*opacity)?$/i,
      (match) => ({ properties: { fillOpacity: parseInt(match[1]) / 100 } })
    );

    this.commandPatterns.set(
      /^(?:half|50%?)\s*fill(?:\s*opacity)?$/i,
      () => ({ properties: { fillOpacity: 0.5 } })
    );

    this.commandPatterns.set(
      /^fill\s*(?:half|50%?)(?:\s*opacity)?$/i,
      () => ({ properties: { fillOpacity: 0.5 } })
    );

    this.commandPatterns.set(
      /^full\s*fill(?:\s*opacity)?$/i,
      () => ({ properties: { fillOpacity: 1 } })
    );

    this.commandPatterns.set(
      /^fill\s*full(?:\s*opacity)?$/i,
      () => ({ properties: { fillOpacity: 1 } })
    );

    this.commandPatterns.set(
      /^(?:make\s*)?fill\s*(?:semi[- ]?)?transparent$/i,
      () => ({ properties: { fillOpacity: 0.5 } })
    );

    // ========================================================================
    // TOOLTIP PATTERNS
    // Custom hover tooltips for non-chart/map components
    // Note: Chart/Map components use showTooltip (boolean) via their own handlers
    // ========================================================================

    // Pattern: "add tooltip 'text'" or 'add tooltip "text"'
    this.commandPatterns.set(
      /^add\s+tooltip\s+["'](.+)["']$/i,
      (match, component) => {
        if (this.hasNativeTooltipSystem(component)) return null;
        return {
          properties: {
            tooltip: { text: match[1], enabled: true } as TooltipConfig
          }
        };
      }
    );

    // Pattern: "add tooltip" (enables tooltip with placeholder text)
    this.commandPatterns.set(
      /^add\s+tooltip$/i,
      (_, component) => {
        if (this.hasNativeTooltipSystem(component)) return null;
        return {
          properties: {
            tooltip: { text: 'Tooltip', enabled: true } as TooltipConfig
          }
        };
      }
    );

    // Pattern: "set tooltip to 'text'" or "tooltip 'text'" or "set tooltip 'text'"
    this.commandPatterns.set(
      /^(?:set\s+)?tooltip\s+(?:to\s+)?["'](.+)["']$/i,
      (match, component) => {
        if (this.hasNativeTooltipSystem(component)) return null;
        return {
          properties: {
            tooltip: { text: match[1], enabled: true } as TooltipConfig
          }
        };
      }
    );

    // Pattern: "change tooltip to 'text'" or "change tooltip 'text'"
    this.commandPatterns.set(
      /^change\s+tooltip\s+(?:to\s+)?["'](.+)["']$/i,
      (match, component) => {
        if (this.hasNativeTooltipSystem(component)) return null;
        return {
          properties: {
            tooltip: { text: match[1], enabled: true } as TooltipConfig
          }
        };
      }
    );

    // Pattern: "remove tooltip" or "no tooltip" or "delete tooltip" or "clear tooltip"
    this.commandPatterns.set(
      /^(?:remove|no|delete|clear)\s+tooltip$/i,
      (_, component) => {
        if (this.hasNativeTooltipSystem(component)) return null;
        return { properties: { tooltip: null } };
      }
    );

    // Pattern: "hide tooltip" - keeps text but disables display
    this.commandPatterns.set(
      /^hide\s+tooltip$/i,
      (_, component) => {
        if (this.hasNativeTooltipSystem(component)) return null;
        const existingTooltip = component.properties?.tooltip as TooltipConfig | undefined;
        return {
          properties: {
            tooltip: {
              text: existingTooltip?.text || '',
              enabled: false,
              side: existingTooltip?.side,
              delayDuration: existingTooltip?.delayDuration
            } as TooltipConfig
          }
        };
      }
    );

    // Pattern: "show tooltip" - re-enables existing tooltip
    this.commandPatterns.set(
      /^show\s+tooltip$/i,
      (_, component) => {
        if (this.hasNativeTooltipSystem(component)) return null;
        const existingTooltip = component.properties?.tooltip as TooltipConfig | undefined;
        return {
          properties: {
            tooltip: {
              text: existingTooltip?.text || 'Tooltip',
              enabled: true,
              side: existingTooltip?.side,
              delayDuration: existingTooltip?.delayDuration
            } as TooltipConfig
          }
        };
      }
    );

    // Pattern: "tooltip top/right/bottom/left" - set tooltip position
    this.commandPatterns.set(
      /^tooltip\s+(top|right|bottom|left)$/i,
      (match, component) => {
        if (this.hasNativeTooltipSystem(component)) return null;
        const existingTooltip = component.properties?.tooltip as TooltipConfig | undefined;
        return {
          properties: {
            tooltip: {
              text: existingTooltip?.text || 'Tooltip',
              enabled: true,
              side: match[1].toLowerCase() as 'top' | 'right' | 'bottom' | 'left',
              delayDuration: existingTooltip?.delayDuration
            } as TooltipConfig
          }
        };
      }
    );

    // Shortcut: "tt 'text'" - quick tooltip add
    this.commandPatterns.set(
      /^tt\s+["'](.+)["']$/i,
      (match, component) => {
        if (this.hasNativeTooltipSystem(component)) return null;
        return {
          properties: {
            tooltip: { text: match[1], enabled: true } as TooltipConfig
          }
        };
      }
    );

    // Shortcut: "tt off" or "tt none" or "tt remove" - quick tooltip remove
    this.commandPatterns.set(
      /^tt\s+(?:off|none|remove)$/i,
      (_, component) => {
        if (this.hasNativeTooltipSystem(component)) return null;
        return { properties: { tooltip: null } };
      }
    );
  }

  /**
   * Phase 2.2: Initialize relative/comparative modification patterns
   * These patterns calculate changes based on current component values
   */
  private initializeRelativePatterns(): void {
    // ========================================================================
    // PERCENTAGE-BASED SIZE MODIFIERS
    // ========================================================================

    // "50% larger", "25% bigger", "100% larger"
    this.commandPatterns.set(
      /^(\d+)%\s*(?:larger|bigger)$/i,
      (match, component) => {
        const percent = parseInt(match[1]);
        const multiplier = 1 + percent / 100;
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(currentWidth * multiplier, 'size'),
            height: clampValue(currentHeight * multiplier, 'size')
          }
        };
      }
    );

    // "50% smaller", "25% tinier"
    this.commandPatterns.set(
      /^(\d+)%\s*(?:smaller|tinier)$/i,
      (match, component) => {
        const percent = parseInt(match[1]);
        const multiplier = 1 - percent / 100;
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(Math.max(10, currentWidth * multiplier), 'size'),
            height: clampValue(Math.max(10, currentHeight * multiplier), 'size')
          }
        };
      }
    );

    // ========================================================================
    // MULTIPLIER-BASED SIZE MODIFIERS
    // ========================================================================

    // "double the size", "double size", "double it"
    this.commandPatterns.set(
      /^double\s*(?:the\s*)?(?:size|it)?$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(currentWidth * 2, 'size'),
            height: clampValue(currentHeight * 2, 'size')
          }
        };
      }
    );

    // "triple the size", "triple size"
    this.commandPatterns.set(
      /^triple\s*(?:the\s*)?(?:size|it)?$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(currentWidth * 3, 'size'),
            height: clampValue(currentHeight * 3, 'size')
          }
        };
      }
    );

    // "half the size", "halve it", "half size"
    this.commandPatterns.set(
      /^(?:half|halve)\s*(?:the\s*)?(?:size|it)?$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(currentWidth / 2, 'size'),
            height: clampValue(currentHeight / 2, 'size')
          }
        };
      }
    );

    // ========================================================================
    // QUALITATIVE SIZE MODIFIERS
    // ========================================================================

    // "a bit bigger", "a little larger", "slightly bigger"
    this.commandPatterns.set(
      /^(?:a\s*)?(?:bit|little|slightly)\s*(?:bigger|larger)$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(currentWidth * 1.1, 'size'),
            height: clampValue(currentHeight * 1.1, 'size')
          }
        };
      }
    );

    // "a bit smaller", "a little tinier", "slightly smaller"
    this.commandPatterns.set(
      /^(?:a\s*)?(?:bit|little|slightly)\s*(?:smaller|tinier)$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(currentWidth * 0.9, 'size'),
            height: clampValue(currentHeight * 0.9, 'size')
          }
        };
      }
    );

    // "much bigger", "much larger", "way bigger"
    this.commandPatterns.set(
      /^(?:much|way|a\s*lot)\s*(?:bigger|larger)$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(currentWidth * 1.5, 'size'),
            height: clampValue(currentHeight * 1.5, 'size')
          }
        };
      }
    );

    // "much smaller", "way smaller"
    this.commandPatterns.set(
      /^(?:much|way|a\s*lot)\s*(?:smaller|tinier)$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(currentWidth * 0.5, 'size'),
            height: clampValue(currentHeight * 0.5, 'size')
          }
        };
      }
    );

    // ========================================================================
    // INCREMENTAL MODIFIERS (px-based)
    // ========================================================================

    // "increase by 8px", "increase by 8", "grow by 10px"
    this.commandPatterns.set(
      /^(?:increase|grow|expand)\s*(?:by\s*)?(\d+)\s*(?:px)?$/i,
      (match, component) => {
        const amount = parseInt(match[1]);
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(currentWidth + amount, 'size'),
            height: clampValue(currentHeight + amount, 'size')
          }
        };
      }
    );

    // "decrease by 8px", "shrink by 10"
    this.commandPatterns.set(
      /^(?:decrease|shrink|reduce)\s*(?:by\s*)?(\d+)\s*(?:px)?$/i,
      (match, component) => {
        const amount = parseInt(match[1]);
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(currentWidth - amount, 'size'),
            height: clampValue(currentHeight - amount, 'size')
          }
        };
      }
    );

    // ========================================================================
    // DIMENSION-SPECIFIC MODIFIERS
    // ========================================================================

    // "50% wider", "25% wider"
    this.commandPatterns.set(
      /^(\d+)%\s*wider$/i,
      (match, component) => {
        const percent = parseInt(match[1]);
        const currentWidth = getCurrentWidth(component);
        return {
          size: {
            width: clampValue(currentWidth * (1 + percent / 100), 'size')
          }
        };
      }
    );

    // "50% narrower", "25% narrower"
    this.commandPatterns.set(
      /^(\d+)%\s*narrower$/i,
      (match, component) => {
        const percent = parseInt(match[1]);
        const currentWidth = getCurrentWidth(component);
        return {
          size: {
            width: clampValue(currentWidth * (1 - percent / 100), 'size')
          }
        };
      }
    );

    // "50% taller", "25% taller"
    this.commandPatterns.set(
      /^(\d+)%\s*taller$/i,
      (match, component) => {
        const percent = parseInt(match[1]);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            height: clampValue(currentHeight * (1 + percent / 100), 'size')
          }
        };
      }
    );

    // "50% shorter", "25% shorter"
    this.commandPatterns.set(
      /^(\d+)%\s*shorter$/i,
      (match, component) => {
        const percent = parseInt(match[1]);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            height: clampValue(currentHeight * (1 - percent / 100), 'size')
          }
        };
      }
    );

    // "twice as wide", "double the width"
    this.commandPatterns.set(
      /^(?:twice\s*(?:as\s*)?wide|double\s*(?:the\s*)?width)$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        return {
          size: {
            width: clampValue(currentWidth * 2, 'size')
          }
        };
      }
    );

    // "twice as tall", "double the height"
    this.commandPatterns.set(
      /^(?:twice\s*(?:as\s*)?tall|double\s*(?:the\s*)?height)$/i,
      (_, component) => {
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            height: clampValue(currentHeight * 2, 'size')
          }
        };
      }
    );

    // "half as wide", "half the width"
    this.commandPatterns.set(
      /^(?:half\s*(?:as\s*)?wide|half\s*(?:the\s*)?width)$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        return {
          size: {
            width: clampValue(currentWidth / 2, 'size')
          }
        };
      }
    );

    // "half as tall", "half the height"
    this.commandPatterns.set(
      /^(?:half\s*(?:as\s*)?tall|half\s*(?:the\s*)?height)$/i,
      (_, component) => {
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            height: clampValue(currentHeight / 2, 'size')
          }
        };
      }
    );

    // "wider", "make it wider", "make this wider" (simple qualitative - 25% increase)
    // Phase 5.2: Updated to match conversational phrases and reduced from 50% to 25% for more subtle changes
    this.commandPatterns.set(
      /^(?:make\s+(?:it|this)\s+)?wider$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const newWidth = Math.round(currentWidth * 1.25); // 25% increase
        return {
          size: {
            width: clampValue(newWidth, 'size')
          }
        };
      }
    );

    // "narrower", "make it narrower", "make this narrower" (simple qualitative - 15% decrease)
    // Phase 5.2: Updated to match conversational phrases and reduced from 25% to 15% for more subtle changes
    this.commandPatterns.set(
      /^(?:make\s+(?:it|this)\s+)?narrower$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const newWidth = Math.round(currentWidth * 0.85); // 15% decrease
        return {
          size: {
            width: clampValue(newWidth, 'size')
          }
        };
      }
    );

    // "taller", "make it taller", "make this taller" (simple qualitative - 25% increase)
    // Phase 5.2: Updated to match conversational phrases and reduced from 50% to 25% for more subtle changes
    this.commandPatterns.set(
      /^(?:make\s+(?:it|this)\s+)?taller$/i,
      (_, component) => {
        const currentHeight = getCurrentHeight(component);
        const newHeight = Math.round(currentHeight * 1.25); // 25% increase
        return {
          size: {
            height: clampValue(newHeight, 'size')
          }
        };
      }
    );

    // "shorter", "make it shorter", "make this shorter" (simple qualitative - 15% decrease)
    // Phase 5.2: Reduced from 25% to 15% for more subtle changes
    this.commandPatterns.set(
      /^(?:make\s+(?:it|this)\s+)?shorter$/i,
      (_, component) => {
        const currentHeight = getCurrentHeight(component);
        const newHeight = Math.round(currentHeight * 0.85); // 15% decrease
        return {
          size: {
            height: clampValue(newHeight, 'size')
          }
        };
      }
    );

    // ========================================================================
    // OPACITY MODIFIERS
    // ========================================================================

    // "slightly more transparent", "a bit more transparent"
    this.commandPatterns.set(
      /^(?:slightly|a\s*(?:bit|little))\s*more\s*transparent$/i,
      (_, component) => {
        const currentOpacity = parseStyleValue(component.style?.opacity, 1);
        const newOpacity = Math.max(0, currentOpacity - 0.1);
        return {
          style: { opacity: clampValue(newOpacity, 'opacity') }
        };
      }
    );

    // "slightly less transparent", "a bit less transparent" (more opaque)
    this.commandPatterns.set(
      /^(?:slightly|a\s*(?:bit|little))\s*less\s*transparent$/i,
      (_, component) => {
        const currentOpacity = parseStyleValue(component.style?.opacity, 1);
        const newOpacity = Math.min(1, currentOpacity + 0.1);
        return {
          style: { opacity: clampValue(newOpacity, 'opacity') }
        };
      }
    );

    // "more transparent", "fade more"
    this.commandPatterns.set(
      /^(?:more\s*transparent|fade\s*(?:more|it))$/i,
      (_, component) => {
        const currentOpacity = parseStyleValue(component.style?.opacity, 1);
        const newOpacity = Math.max(0, currentOpacity - 0.2);
        return {
          style: { opacity: clampValue(newOpacity, 'opacity') }
        };
      }
    );

    // "less transparent", "more opaque", "more visible"
    this.commandPatterns.set(
      /^(?:less\s*transparent|more\s*opaque|more\s*visible)$/i,
      (_, component) => {
        const currentOpacity = parseStyleValue(component.style?.opacity, 1);
        const newOpacity = Math.min(1, currentOpacity + 0.2);
        return {
          style: { opacity: clampValue(newOpacity, 'opacity') }
        };
      }
    );

    // ========================================================================
    // PADDING/MARGIN RELATIVE MODIFIERS
    // ========================================================================

    // "more padding", "add padding"
    this.commandPatterns.set(
      /^(?:more|add|increase)\s*padding$/i,
      (_, component) => {
        const currentPadding = parseStyleValue(component.style?.padding, 0);
        const newPadding = currentPadding + 8; // Add 8px (1 spacing unit)
        return {
          style: { padding: `${clampValue(newPadding, 'padding')}px` }
        };
      }
    );

    // "less padding", "reduce padding"
    this.commandPatterns.set(
      /^(?:less|reduce|decrease)\s*padding$/i,
      (_, component) => {
        const currentPadding = parseStyleValue(component.style?.padding, 16);
        const newPadding = Math.max(0, currentPadding - 8);
        return {
          style: { padding: `${clampValue(newPadding, 'padding')}px` }
        };
      }
    );

    // "more margin", "add margin"
    this.commandPatterns.set(
      /^(?:more|add|increase)\s*margin$/i,
      (_, component) => {
        const currentMargin = parseStyleValue(component.style?.margin, 0);
        const newMargin = currentMargin + 8;
        return {
          style: { margin: `${clampValue(newMargin, 'margin')}px` }
        };
      }
    );

    // "less margin", "reduce margin"
    this.commandPatterns.set(
      /^(?:less|reduce|decrease)\s*margin$/i,
      (_, component) => {
        const currentMargin = parseStyleValue(component.style?.margin, 16);
        const newMargin = Math.max(0, currentMargin - 8);
        return {
          style: { margin: `${clampValue(newMargin, 'margin')}px` }
        };
      }
    );

    // ========================================================================
    // GAP RELATIVE MODIFIERS (for flex containers)
    // ========================================================================

    // "more gap", "increase gap", "bigger gap"
    this.commandPatterns.set(
      /^(?:more|increase|bigger)\s*gap$/i,
      (_, component) => {
        const currentGap = parseStyleValue(component.style?.gap, 0);
        const newGap = currentGap + 8;
        return {
          style: { gap: `${clampValue(newGap, 'gap')}px` }
        };
      }
    );

    // "less gap", "decrease gap", "smaller gap"
    this.commandPatterns.set(
      /^(?:less|decrease|smaller)\s*gap$/i,
      (_, component) => {
        const currentGap = parseStyleValue(component.style?.gap, 16);
        const newGap = Math.max(0, currentGap - 8);
        return {
          style: { gap: `${clampValue(newGap, 'gap')}px` }
        };
      }
    );

    // ========================================================================
    // BORDER RADIUS RELATIVE MODIFIERS
    // ========================================================================

    // "more rounded", "rounder"
    this.commandPatterns.set(
      /^(?:more\s*)?round(?:er|ed)$/i,
      (_, component) => {
        const currentRadius = parseStyleValue(component.style?.borderRadius, 0);
        const newRadius = currentRadius + 4;
        return {
          style: { borderRadius: `${clampValue(newRadius, 'borderRadius')}px` }
        };
      }
    );

    // "less rounded", "sharper corners"
    this.commandPatterns.set(
      /^(?:less\s*rounded|sharper(?:\s*corners)?)$/i,
      (_, component) => {
        const currentRadius = parseStyleValue(component.style?.borderRadius, 8);
        const newRadius = Math.max(0, currentRadius - 4);
        return {
          style: { borderRadius: `${clampValue(newRadius, 'borderRadius')}px` }
        };
      }
    );

    // ========================================================================
    // SIMPLE SIZE MODIFIERS (no qualifiers)
    // ========================================================================

    // "bigger", "larger" (simple - 50% increase)
    this.commandPatterns.set(
      /^(?:bigger|larger)$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        const newWidth = Math.round(currentWidth * 1.5);
        const newHeight = Math.round(currentHeight * 1.5);
        return {
          size: {
            width: clampValue(newWidth, 'size'),
            height: clampValue(newHeight, 'size')
          }
        };
      }
    );

    // "smaller", "tinier" (simple - 25% decrease)
    this.commandPatterns.set(
      /^(?:smaller|tinier)$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        const newWidth = Math.round(currentWidth * 0.75);
        const newHeight = Math.round(currentHeight * 0.75);
        return {
          size: {
            width: clampValue(newWidth, 'size'),
            height: clampValue(newHeight, 'size')
          }
        };
      }
    );

    // "make it bigger", "make bigger"
    this.commandPatterns.set(
      /^make\s*(?:it\s*)?(?:bigger|larger)$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(currentWidth * 1.2, 'size'),
            height: clampValue(currentHeight * 1.2, 'size')
          }
        };
      }
    );

    // "make it smaller", "make smaller"
    this.commandPatterns.set(
      /^make\s*(?:it\s*)?(?:smaller|tinier)$/i,
      (_, component) => {
        const currentWidth = getCurrentWidth(component);
        const currentHeight = getCurrentHeight(component);
        return {
          size: {
            width: clampValue(currentWidth * 0.8, 'size'),
            height: clampValue(currentHeight * 0.8, 'size')
          }
        };
      }
    );

  }

  // ============================================================================
  // Phase 3.1: Breakpoint-Specific Command Patterns
  // ============================================================================

  /**
   * Extract breakpoint from a command like "full width on mobile" or "hide on desktop"
   * Returns the breakpoint ID and the command without the breakpoint suffix
   * The base command is also normalized to strip pronouns like "this", "it"
   */
  extractBreakpointFromCommand(command: string): { breakpoint: BreakpointId | null; baseCommand: string } {
    const normalizedCommand = command.trim().toLowerCase();

    // Helper to normalize base command (strip "this", "it", etc.)
    const normalizeBaseCommand = (cmd: string): string => {
      let normalized = cmd.trim();
      // Strip object pronouns: "hide this" → "hide", "show it" → "show"
      normalized = normalized.replace(/\s+(this|it|that|the component)$/i, '').trim();
      return normalized;
    };

    // Pattern: "X on mobile/tablet/desktop"
    const onBreakpointMatch = normalizedCommand.match(/^(.+?)\s+on\s+(mobile|tablet|desktop|phone|phones|small screens?|medium|large|laptop|computer|ipad|big screens?)\s*(?:only)?$/i);
    if (onBreakpointMatch) {
      const baseCommand = normalizeBaseCommand(onBreakpointMatch[1]);
      const breakpointRef = onBreakpointMatch[2].trim();
      const breakpoint = parseBreakpointReference(breakpointRef);
      if (breakpoint && breakpoint !== 'all') {
        return { breakpoint, baseCommand };
      }
    }

    // Pattern: "on mobile/tablet/desktop, X"
    const prefixBreakpointMatch = normalizedCommand.match(/^on\s+(mobile|tablet|desktop|phone|phones|small screens?|medium|large|laptop|computer|ipad|big screens?)[,:]?\s+(.+)$/i);
    if (prefixBreakpointMatch) {
      const breakpointRef = prefixBreakpointMatch[1].trim();
      const baseCommand = normalizeBaseCommand(prefixBreakpointMatch[2]);
      const breakpoint = parseBreakpointReference(breakpointRef);
      if (breakpoint && breakpoint !== 'all') {
        return { breakpoint, baseCommand };
      }
    }

    // Pattern: "for mobile/tablet/desktop: X"
    const forBreakpointMatch = normalizedCommand.match(/^for\s+(mobile|tablet|desktop|phone|phones|small screens?|medium|large|laptop|computer|ipad|big screens?)[,:]?\s+(.+)$/i);
    if (forBreakpointMatch) {
      const breakpointRef = forBreakpointMatch[1].trim();
      const baseCommand = normalizeBaseCommand(forBreakpointMatch[2]);
      const breakpoint = parseBreakpointReference(breakpointRef);
      if (breakpoint && breakpoint !== 'all') {
        return { breakpoint, baseCommand };
      }
    }

    // Pattern: "mobile/tablet/desktop only: X"
    const onlyPrefixMatch = normalizedCommand.match(/^(mobile|tablet|desktop|phone|phones|small screens?|medium|large|laptop|computer|ipad|big screens?)\s+only[,:]?\s+(.+)$/i);
    if (onlyPrefixMatch) {
      const breakpointRef = onlyPrefixMatch[1].trim();
      const baseCommand = normalizeBaseCommand(onlyPrefixMatch[2]);
      const breakpoint = parseBreakpointReference(breakpointRef);
      if (breakpoint && breakpoint !== 'all') {
        return { breakpoint, baseCommand };
      }
    }

    return { breakpoint: null, baseCommand: command };
  }

  /**
   * Parse command for split breakpoint patterns like:
   * "stack on mobile, horizontal on desktop"
   * "hide on mobile, show on desktop"
   */
  parseSplitBreakpointCommand(command: string): { responsiveStyles: Record<string, PropertyChange> } | null {
    const normalizedCommand = command.trim().toLowerCase();

    // Pattern: "X on mobile, Y on desktop" or "X on mobile, Y on tablet, Z on desktop"
    const splitMatch = normalizedCommand.match(/^(.+?)\s+on\s+(mobile|tablet|desktop)[,]\s+(.+?)\s+on\s+(mobile|tablet|desktop)(?:[,]\s+(.+?)\s+on\s+(mobile|tablet|desktop))?$/i);

    if (splitMatch) {
      const responsiveStyles: Record<string, PropertyChange> = {};

      // Parse first part
      const bp1 = parseBreakpointReference(splitMatch[2]);
      if (bp1 && bp1 !== 'all') {
        const changes1 = this.matchPatterns(splitMatch[1].trim(), {} as Component);
        if (changes1) {
          responsiveStyles[bp1] = changes1;
        }
      }

      // Parse second part
      const bp2 = parseBreakpointReference(splitMatch[4]);
      if (bp2 && bp2 !== 'all') {
        const changes2 = this.matchPatterns(splitMatch[3].trim(), {} as Component);
        if (changes2) {
          responsiveStyles[bp2] = changes2;
        }
      }

      // Parse optional third part
      if (splitMatch[5] && splitMatch[6]) {
        const bp3 = parseBreakpointReference(splitMatch[6]);
        if (bp3 && bp3 !== 'all') {
          const changes3 = this.matchPatterns(splitMatch[5].trim(), {} as Component);
          if (changes3) {
            responsiveStyles[bp3] = changes3;
          }
        }
      }

      if (Object.keys(responsiveStyles).length > 0) {
        return { responsiveStyles };
      }
    }

    return null;
  }

  /**
   * Wrap property changes with breakpoint info
   */
  wrapWithBreakpoint(changes: PropertyChange, breakpoint: BreakpointId): PropertyChange {
    return {
      ...changes,
      breakpoint,
    };
  }

  /**
   * Check if this is a breakpoint-specific command
   */
  isBreakpointCommand(command: string): boolean {
    const normalizedCommand = command.trim().toLowerCase();

    // Check for "on mobile/tablet/desktop" suffix
    if (/\s+on\s+(mobile|tablet|desktop|phone|phones|small screens?|medium|large|laptop|computer|ipad|big screens?)\s*(only)?$/i.test(normalizedCommand)) {
      return true;
    }

    // Check for "on mobile/tablet/desktop, ..." prefix
    if (/^on\s+(mobile|tablet|desktop|phone|phones|small screens?|medium|large|laptop|computer|ipad|big screens?)[,:]/i.test(normalizedCommand)) {
      return true;
    }

    // Check for "for mobile/tablet/desktop: ..." prefix
    if (/^for\s+(mobile|tablet|desktop|phone|phones|small screens?|medium|large|laptop|computer|ipad|big screens?)[,:]/i.test(normalizedCommand)) {
      return true;
    }

    // Check for "mobile/tablet/desktop only: ..." prefix
    if (/^(mobile|tablet|desktop|phone|phones|small screens?|medium|large|laptop|computer|ipad|big screens?)\s+only[,:]/i.test(normalizedCommand)) {
      return true;
    }

    // Check for split breakpoint pattern "X on mobile, Y on desktop"
    if (/\s+on\s+(mobile|tablet|desktop)[,]\s+.+\s+on\s+(mobile|tablet|desktop)/i.test(normalizedCommand)) {
      return true;
    }

    return false;
  }

  /**
   * Parse a breakpoint-specific command and return property changes with breakpoint info
   */
  parseBreakpointCommand(command: string, component: Component): PropertyChange | null {
    // First check for split breakpoint commands
    const splitResult = this.parseSplitBreakpointCommand(command);
    if (splitResult) {
      // Convert split styles to ResponsiveStyleChanges format
      const responsiveStyles: ResponsiveStyleChanges = {};
      for (const [bp, changes] of Object.entries(splitResult.responsiveStyles)) {
        const merged: Record<string, unknown> = { ...changes.style, ...changes.properties };
        // Include size changes if present
        if (changes.size) {
          merged.width = changes.size.width;
          merged.height = changes.size.height;
        }
        if (bp === 'mobile') responsiveStyles.mobile = merged;
        else if (bp === 'tablet') responsiveStyles.tablet = merged;
        else if (bp === 'desktop') responsiveStyles.desktop = merged;
      }

      return { responsiveStyles };
    }

    // Extract breakpoint from single-breakpoint command
    const { breakpoint, baseCommand } = this.extractBreakpointFromCommand(command);

    if (!breakpoint) {
      return null;
    }

    // Parse the base command without breakpoint suffix
    const changes = this.matchPatternsNormalized(baseCommand, component);

    if (changes) {
      const result = this.wrapWithBreakpoint(changes, breakpoint);
      return result;
    }

    return null;
  }

  // ============================================================================
  // Phase 2.3: Style Cloning Methods
  // ============================================================================

  /**
   * Check if a command is a style clone request
   * Returns the parsed clone info if it's a clone command, null otherwise
   */
  isCloneCommand(command: string): { sourceRef: string; scope: StyleCloneScope } | null {
    return styleCloneService.parseCloneCommand(command);
  }

  /**
   * Handle a style clone command
   * Clones styles from a source component to the target component
   */
  async handleCloneCommand(
    command: string,
    targetComponent: Component,
    context?: {
      selectedComponents?: Component[];
      templateInstanceId?: string;
      pageId?: string;
    }
  ): Promise<CommandResult> {
    const cloneInfo = this.isCloneCommand(command);
    if (!cloneInfo) {
      return {
        success: false,
        error: 'Not a style clone command'
      };
    }

    const result = await styleCloneService.cloneStyles({
      sourceRef: cloneInfo.sourceRef,
      targetComponent,
      propertyScope: cloneInfo.scope,
      context
    });

    if (!result.success) {
      return {
        success: false,
        error: result.error,
        suggestions: styleCloneService.getSuggestions(context?.selectedComponents || [])
      };
    }

    // Generate description of what was cloned
    const description = styleCloneService.getCloneDescription(result, cloneInfo.scope);
    // Log warnings if any properties were skipped
    if (result.warnings.length > 0) {
    }

    return {
      success: true,
      command: {
        type: 'style',
        changes: result.clonedStyles,
        confidence: 0.92,
        description
      }
    };
  }

  // ============================================================================
  // Phase 3.2: Multi-Select Operations
  // ============================================================================

  /**
   * Multi-select command patterns for alignment, distribution, matching, stacking
   * Supports natural language variations like "align both components to the left"
   */
  private static readonly MULTI_SELECT_PATTERNS: Array<[RegExp, string]> = [
    // Alignment patterns - flexible to handle natural language
    [/align.*(?:to\s*(?:the\s*)?)?left/i, 'align'],
    [/align.*(?:to\s*(?:the\s*)?)?right/i, 'align'],
    [/align.*(?:to\s*(?:the\s*)?)?top/i, 'align'],
    [/align.*(?:to\s*(?:the\s*)?)?bottom/i, 'align'],
    [/align.*(?:to\s*(?:the\s*)?)?center/i, 'align'],
    [/align.*(?:to\s*(?:the\s*)?)?middle/i, 'align'],
    [/align.*horizontally/i, 'align'],
    [/align.*vertically/i, 'align'],
    // Simple forms
    [/^align\s*(left|right|top|bottom|center|middle)$/i, 'align'],

    // Distribution patterns
    [/(distribute|space).*(?:evenly|equally)/i, 'distribute'],
    [/(distribute|space).*horizontally/i, 'distribute'],
    [/(distribute|space).*vertically/i, 'distribute'],
    [/^equal\s*(spacing|gaps?)$/i, 'distribute'],
    [/^(distribute|space)\s*(evenly|equally)$/i, 'distribute'],

    // Size matching patterns
    [/(match|same|equal).*widths?/i, 'matchSize'],
    [/(match|same|equal).*heights?/i, 'matchSize'],
    [/(match|same|equal).*sizes?/i, 'matchSize'],
    [/make.*same\s*(width|height|size)/i, 'matchSize'],
    [/^(match|same|equal)\s*(widths?|heights?|sizes?)$/i, 'matchSize'],

    // Stacking patterns
    [/stack.*vertically/i, 'stack'],
    [/stack.*horizontally/i, 'stack'],
    [/stack.*(\d+)\s*(?:px)?\s*(?:gaps?|spacing)/i, 'stack'],
    [/^stack$/i, 'stack'],

    // Grouping patterns (future implementation)
    [/^group\s*(these|selected|all|them)?$/i, 'group'],
  ];

  /**
   * Check if a command is a multi-select operation
   * These commands require multiple components to be selected
   */
  isMultiSelectCommand(command: string): boolean {
    const normalizedCommand = command.trim().toLowerCase();

    for (const [pattern] of QuickCommandHandler.MULTI_SELECT_PATTERNS) {
      if (pattern.test(normalizedCommand)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Parse a multi-select command and return the operation
   */
  parseMultiSelectCommand(command: string): MultiSelectOperation | null {
    return multiSelectOperationService.parseMultiSelectCommand(command);
  }

  /**
   * Execute a multi-select operation on the given components
   * @param command The command string
   * @param components The selected components
   * @param allComponents Optional array of all components for parent/sibling lookup
   */
  executeMultiSelectOperation(
    command: string,
    components: Component[],
    allComponents?: Component[]
  ): MultiSelectOperationResult {
    const operation = this.parseMultiSelectCommand(command);

    if (!operation) {
      return {
        success: false,
        affectedComponents: [],
        description: `Command "${command}" is not a recognized multi-select operation`,
      };
    }

    return multiSelectOperationService.executeOperation(operation, components, allComponents);
  }

  /**
   * Get suggestions for multi-select operations
   */
  getMultiSelectSuggestions(): string[] {
    return [
      // Alignment
      'align left',
      'align right',
      'align top',
      'align bottom',
      'align center',
      'align horizontally',
      'align vertically',

      // Distribution
      'distribute evenly',
      'space evenly',
      'distribute horizontally',
      'distribute vertically',

      // Size matching
      'match widths',
      'match heights',
      'same size',
      'equal width',
      'equal height',

      // Stacking
      'stack vertically',
      'stack horizontally',
      'stack with 16px gaps',
      'stack with 20px spacing',
    ];
  }

  // ============================================================================
  // Phase 6.4: Flex-Aware Suggestions
  // ============================================================================

  /**
   * Check if a command is a flex child command
   * @param command The user's command
   */
  isFlexChildCommand(command: string): boolean {
    return flexRelationshipService.isFlexChildCommand(command);
  }

  /**
   * Get suggestions for flex child components
   * @param components The selected components
   * @param allComponents All components for parent lookup
   */
  getFlexChildSuggestions(components: Component[], allComponents: Component[]): string[] {
    if (components.length === 0) {
      return [];
    }

    // Analyze flex relationship for first component
    const flexRelationship = components.length === 1
      ? flexRelationshipService.analyzeFlexRelationship(components[0], allComponents)
      : flexRelationshipService.analyzeMultipleFlexRelationships(components, allComponents);

    if (!flexRelationship || !flexRelationship.isFlexChild) {
      return [];
    }

    return flexRelationshipService.getFlexSuggestions(flexRelationship);
  }

  /**
   * Get context description for flex children
   * @param components The selected components
   * @param allComponents All components for parent lookup
   */
  getFlexContextDescription(components: Component[], allComponents: Component[]): string {
    if (components.length === 0) {
      return '';
    }

    const flexRelationship = components.length === 1
      ? flexRelationshipService.analyzeFlexRelationship(components[0], allComponents)
      : flexRelationshipService.analyzeMultipleFlexRelationships(components, allComponents);

    if (!flexRelationship || !flexRelationship.isFlexChild) {
      return '';
    }

    return flexRelationshipService.getFlexContextDescription(flexRelationship);
  }

  /**
   * Normalize conversational phrases to their core command
   * "Can you make this much bigger?" → "much bigger"
   * "please make it a little smaller" → "a little smaller"
   * "card taller" → "taller" (after semantic resolution)
   * "hide this" → "hide" (strip object pronouns after action verbs)
   */
  private normalizeCommand(command: string): string {
    let normalized = command.trim().toLowerCase();

    // Remove common conversational prefixes
    const prefixes = [
      /^can you (please )?/i,
      /^could you (please )?/i,
      /^would you (please )?/i,
      /^please /i,
      /^i want (you )?to /i,
      /^i'd like (you )?to /i,
      /^make (it |this |that |the component )?/i,
      /^set (it |this |that |the component )?to (be )?/i,
      /^change (it |this |that |the component )?to (be )?/i,
    ];

    for (const prefix of prefixes) {
      normalized = normalized.replace(prefix, '');
    }

    // Remove trailing punctuation and common suffixes
    normalized = normalized.replace(/[?.!]+$/, '').trim();
    normalized = normalized.replace(/ please$/, '').trim();

    // Strip object pronouns after action verbs: "hide this" → "hide", "show it" → "show"
    const actionVerbsWithPronoun = [
      /^(hide|show|display|remove|delete|clear|reset|toggle|enable|disable|lock|unlock)\s+(this|it|that|the component)$/i,
    ];
    for (const pattern of actionVerbsWithPronoun) {
      const match = normalized.match(pattern);
      if (match) {
        normalized = match[1]; // Keep just the verb
      }
    }

    normalized = normalized
      .replace(/^(?:this\s+)?selected\s+component\s+/i, '')
      .replace(/\s+(?:this\s+)?selected\s+component$/i, '')
      .trim();

    // Also strip "this/it/that" suffix from other commands
    normalized = normalized.replace(/\s+(this|it|that|the component)$/i, '').trim();

    // After semantic resolution, commands may start with component references
    // e.g., "card taller" → "taller", "button red" → "red"
    // Strip common component type references from the beginning
    const componentRefs = [
      /^(the )?(card|button|container|text|image|input|chart|gauge|flex|grid|section|header|footer|nav|sidebar|panel|box|wrapper|div|component|element|item|widget|block) /i,
    ];

    for (const ref of componentRefs) {
      normalized = normalized.replace(ref, '');
    }

    return normalized;
  }

  canHandle(command: string, _component: Component): boolean {
    // Quick commands can potentially handle any component
    // Check if command matches any of our patterns
    const trimmedCommand = command.trim().toLowerCase();
    const normalizedCommand = this.normalizeCommand(trimmedCommand);

    console.log(`[QuickCommandHandler.canHandle] Original: "${trimmedCommand}", Normalized: "${normalizedCommand}", patterns count: ${this.commandPatterns.size}`);

    // Phase 3.1: Check for breakpoint-specific commands first
    if (this.isBreakpointCommand(command)) {
      console.log(`[QuickCommandHandler.canHandle] Breakpoint command detected: "${trimmedCommand}"`);
      return true;
    }

    // Phase 2.3: Check for style clone commands
    const cloneInfo = this.isCloneCommand(command);
    if (cloneInfo) {
      console.log(`[QuickCommandHandler.canHandle] Clone command detected: scope="${cloneInfo.scope}", sourceRef="${cloneInfo.sourceRef}"`);
      return true;
    }

    // Try normalized command first (handles conversational phrases)
    for (const [pattern] of this.commandPatterns) {
      if (pattern.test(normalizedCommand)) {
        console.log(`[QuickCommandHandler.canHandle] Pattern matched (normalized): ${pattern}`);
        return true;
      }
    }

    // Also try original command for exact matches
    if (normalizedCommand !== trimmedCommand) {
      for (const [pattern] of this.commandPatterns) {
        if (pattern.test(trimmedCommand)) {
          console.log(`[QuickCommandHandler.canHandle] Pattern matched (original): ${pattern}`);
          return true;
        }
      }
    }

    console.log(`[QuickCommandHandler.canHandle] No pattern matched for: "${normalizedCommand}"`);
    return false;
  }

  /**
   * Public method for matching patterns with normalization
   * Used by PropertyModificationService for quick pattern matching
   */
  public matchPatternsNormalized(command: string, component: Component): PropertyChange | null {
    const trimmedCommand = command.trim();
    const normalizedCommand = this.normalizeCommand(trimmedCommand);

    console.log(`[QuickCommandHandler.matchPatternsNormalized] Original: "${trimmedCommand}", Normalized: "${normalizedCommand}"`);

    // Phase 3.1: Check for breakpoint-specific commands first
    if (this.isBreakpointCommand(command)) {
      console.log(`[QuickCommandHandler.matchPatternsNormalized] Breakpoint command detected`);
      const breakpointResult = this.parseBreakpointCommand(command, component);
      if (breakpointResult) {
        console.log('[QuickCommandHandler.matchPatternsNormalized] Breakpoint command resolved:', breakpointResult);
        return breakpointResult;
      }
    }

    // Try normalized command first
    let changes = this.matchPatterns(normalizedCommand, component);
    if (changes) {
      console.log(`[QuickCommandHandler.matchPatternsNormalized] Matched (normalized):`, changes);
      return changes;
    }

    // Try original command as fallback
    if (normalizedCommand !== trimmedCommand) {
      changes = this.matchPatterns(trimmedCommand, component);
      if (changes) {
        console.log(`[QuickCommandHandler.matchPatternsNormalized] Matched (original):`, changes);
        return changes;
      }
    }

    return null;
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    try {
      const trimmedCommand = command.trim();
      const normalizedCommand = this.normalizeCommand(trimmedCommand);

      // Phase 3.1: Check for breakpoint-specific commands first
      if (this.isBreakpointCommand(command)) {
        console.log('[QuickCommandHandler.parseCommand] Breakpoint command detected');
        const breakpointChanges = this.parseBreakpointCommand(command, component);
        if (breakpointChanges) {
          const validation = this.validateChanges(breakpointChanges, component);
          if (!validation.valid) {
            return {
              success: false,
              error: validation.error,
              suggestions: await this.getSuggestions(component)
            };
          }

          const breakpointName = breakpointChanges.breakpoint
            ? getBreakpointDisplayName(breakpointChanges.breakpoint)
            : 'specified breakpoints';

          return {
            success: true,
            command: {
              type: this.determineCommandType(breakpointChanges),
              changes: breakpointChanges,
              confidence: 0.95,
              description: `Applied changes for ${breakpointName}`
            }
          };
        }
      }

      // Phase 2.3: Check for style clone commands
      const cloneInfo = this.isCloneCommand(command);
      if (cloneInfo) {
        console.log('[QuickCommandHandler.parseCommand] Delegating to clone handler');
        return await this.handleCloneCommand(command, component);
      }

      // Try pattern matching with normalization
      const changes = this.matchPatterns(normalizedCommand, component) ||
                      this.matchPatterns(trimmedCommand, component);
      if (changes) {
        const validation = this.validateChanges(changes, component);
        if (!validation.valid) {
          return {
            success: false,
            error: validation.error,
            suggestions: await this.getSuggestions(component)
          };
        }

        return {
          success: true,
          command: {
            type: this.determineCommandType(changes),
            changes,
            confidence: 0.95, // High confidence for exact pattern matches
            description: this.generateQuickDescription(trimmedCommand, changes)
          }
        };
      }

      return {
        success: false,
        error: "Command not recognized",
        suggestions: await this.getSuggestions(component)
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to parse quick command",
        suggestions: await this.getSuggestions(component)
      };
    }
  }

  private generateQuickDescription(command: string, changes: PropertyChange): string {
    // Generate a user-friendly description of what happened
    const descriptions: string[] = [];

    if (changes.style?.display) {
      descriptions.push(changes.style.display === 'none' ? 'Hidden' : 'Shown');
    }
    if (changes.style?.opacity !== undefined) {
      descriptions.push(`Opacity set to ${changes.style.opacity}`);
    }
    if (changes.style?.color) {
      descriptions.push(`Color changed to ${changes.style.color}`);
    }
    if (changes.style?.backgroundColor) {
      descriptions.push(`Background color changed to ${changes.style.backgroundColor}`);
    }
    if (changes.style?.width || changes.style?.height || changes.size?.width || changes.size?.height) {
      if (changes.size?.width) descriptions.push(`Width set to ${changes.size.width}px`);
      if (changes.size?.height) descriptions.push(`Height set to ${changes.size.height}px`);
      if (changes.style?.width || changes.style?.height) descriptions.push('Size adjusted');
    }
    if (changes.style?.margin || changes.style?.padding) {
      descriptions.push('Spacing adjusted');
    }

    return descriptions.length > 0
      ? `✅ ${descriptions.join(', ')}`
      : `✅ Applied: ${command}`;
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      // Canonical aliases
      'c red', 'bg blue', 'w 200', 'h 100',
      'pd 16', 'mg 20', 'op 50', 'r lg',
      'bd none', 'sh md', 'ts 24',
      'ly flex', 'al center', 'st horizontal', 'gp 12',
      // Natural language patterns
      'hide', 'show', 'transparent', 'opaque',
      'full width', 'half width', 'center',
      'border', 'rounded', 'circle', 'shadow',
      'flex', 'flex row', 'absolute',
      // Phase 2.2: Relative/comparative patterns
      '50% larger', 'double the size', 'a bit bigger',
      '50% wider', 'twice as tall', 'taller', 'shorter',
      'increase by 20', 'slightly more transparent',
      'more padding', 'less margin', 'more gap',
      // Phase 2.3: Style cloning patterns
      'copy the style from header',
      "match the button's colors",
      'use the same font as title',
      'look like the card above',
      'copy spacing from container',
      // Tooltip patterns
      'add tooltip "Click to learn more"',
      'set tooltip to "Hover for details"',
      'remove tooltip',
      'tt "Quick tip"',
      'tooltip left'
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];

    // Context-aware suggestions
    if (component.style?.display === 'none') {
      suggestions.push('show');
    } else {
      suggestions.push('hide');
    }

    if (!component.style?.border || component.style.border === 'none') {
      suggestions.push('border');
    }

    if (!component.style?.boxShadow || component.style.boxShadow === 'none') {
      suggestions.push('shadow');
    }

    if (component.style?.opacity !== undefined && typeof component.style.opacity === 'number' && component.style.opacity < 1) {
      suggestions.push('opaque', 'more opaque');
    } else {
      suggestions.push('50% opacity', 'slightly more transparent');
    }

    // Phase 2.2: Add relative size suggestions based on current dimensions
    const width = component.size?.width;
    if (typeof width === 'number' && width < 500) {
      suggestions.push('50% larger', 'double the size');
    } else if (typeof width === 'number' && width > 200) {
      suggestions.push('50% smaller', 'half the size');
    }

    // Add some common quick commands
    suggestions.push('red', 'blue', 'rounded', 'center');

    // Phase 2.3: Add style cloning suggestions
    suggestions.push("copy the header's style", "match colors from button");

    // Tooltip suggestions (skip for components with native tooltip systems)
    if (!QuickCommandHandler.NATIVE_TOOLTIP_TYPES.has(component.type)) {
      const existingTooltip = component.properties?.tooltip as TooltipConfig | undefined;

      if (!existingTooltip?.text) {
        suggestions.push('add tooltip "Description text"');
      } else {
        suggestions.push('change tooltip "New text"');
        suggestions.push('remove tooltip');
        if (existingTooltip.enabled !== false) {
          suggestions.push('hide tooltip');
        } else {
          suggestions.push('show tooltip');
        }
      }
    }

    return suggestions.slice(0, 10);
  }
}

export const quickCommandHandler = new QuickCommandHandler();
